import { z } from 'zod'
import { sendModeSchema } from './report'
import { issueFailureReasonSchema, issueOriginSchema, issueStatusSchema } from './report-issue'

/**
 * The kill switch (CLI-741, tech sync 2026-09-24: "non-negotiable for v0"). Two switches, one
 * per queue: `generation` halts the tick creating and building automatic issues, `delivery` halts
 * every send — scheduled, automatic, and the Send button — without cancelling anything. Flipping
 * a switch back on resumes: paused deliveries are re-scheduled by the next tick, paused builds are
 * queued by it. Stored in the app database so it takes effect on every instance at once, no
 * deploy, and read by the webhooks on every delivery.
 */
export const autoSendSwitchNameSchema = z.enum(['generation', 'delivery'])
export type AutoSendSwitchName = z.infer<typeof autoSendSwitchNameSchema>

export const autoSendSwitchSchema = z.object({
  paused: z.boolean(),
  reason: z.string().nullable(),
  updatedBy: z.string().nullable(),
  updatedAt: z.iso.datetime().nullable(),
})
export type AutoSendSwitch = z.infer<typeof autoSendSwitchSchema>

export const autoSendSwitchesSchema = z.object({
  generation: autoSendSwitchSchema,
  delivery: autoSendSwitchSchema,
})
export type AutoSendSwitches = z.infer<typeof autoSendSwitchesSchema>

export const setAutoSendSwitchSchema = z.object({
  paused: z.boolean(),
  /** Why — shown to everyone looking at the delivery queue while it is paused. */
  reason: z.string().max(500).optional(),
})
export type SetAutoSendSwitch = z.infer<typeof setAutoSendSwitchSchema>

/**
 * What `GET /api/internal/auto-send/canary` reports (behind the service token): the one report
 * an environment names as its canary — a report on an automatic schedule, sent daily to an
 * internal address so the real cron, QStash and SendGrid are exercised every day — and its latest
 * issue, in the fields the daily check judges (apps/api/scripts/canary-checks.ts): was today's
 * issue created, built with stories, sent at its moment with a provider id, never failed, never
 * late. Recipients are reported as a count and their domains, never the addresses.
 */
export const autoSendCanaryResponseSchema = z.object({
  report: z.object({
    id: z.uuid(),
    name: z.string(),
    status: z.string(),
    sendMode: sendModeSchema,
    schedule: z.string(),
    timezone: z.string(),
    sendTimes: z.array(z.string()),
    recipientCount: z.number().int().nonnegative(),
    recipientDomains: z.array(z.string()),
  }),
  /** Null until the tick has created an issue for the report. */
  issue: z
    .object({
      id: z.uuid(),
      status: issueStatusSchema,
      origin: issueOriginSchema,
      issueDate: z.string(),
      targetSendAt: z.iso.datetime().nullable(),
      scheduledFor: z.iso.datetime().nullable(),
      sentAt: z.iso.datetime().nullable(),
      failedAt: z.iso.datetime().nullable(),
      failureReason: issueFailureReasonSchema.nullable(),
      lastError: z.string().nullable(),
      lateAlertedAt: z.iso.datetime().nullable(),
      generateAttempts: z.number().int().nonnegative(),
      sendAttempts: z.number().int().nonnegative(),
      keptStories: z.number().int().nonnegative(),
      /** The newest real send (mode `send`), or null when none has been attempted. */
      send: z
        .object({
          status: z.string(),
          sentAt: z.iso.datetime(),
          providerMessageId: z.string().nullable(),
          recipientCount: z.number().int().nonnegative(),
          error: z.string().nullable(),
        })
        .nullable(),
    })
    .nullable(),
  now: z.iso.datetime(),
})
export type AutoSendCanaryResponse = z.infer<typeof autoSendCanaryResponseSchema>
